import React, { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { CheckoutProps } from '../../types';
import InputField from '@/components/atoms/floating-input';
import SelectField from '@/components/atoms/floating-select';

const DEFAULT_DATA = {};

const AddressesStep = ({
  initialData = DEFAULT_DATA,
  setDetails,
  details,
  validateForm,
  addresses,
  setValidateForm
}: Pick<CheckoutProps, 'addresses' | 'initialData' | 'setDetails' | 'details' | 'validateForm' | 'setValidateForm'> & { validateForm?: boolean }) => {

  const { locale } = useParams();
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [postcodeError, setPostcodeError] = useState<string>('');

  const countryFromLocale = locale === 'au' ? 'AU' : locale === 'nz' ? 'NZ' : 'AU';

  const requiredFields = ['firstName', 'lastName', 'streetName', 'city', 'postCode', 'country', 'region', 'phone', 'address'];

  const handleShippingAddressChange = (e: any) => {
    setValidateForm(false)
    let name, value;
    if (e?.target) {
      ({ name, value } = e.target);
    } else if (e?.name && e?.value !== undefined) {
      ({ name, value } = e);
    } else {
      console.warn("Unexpected input event:", e);
      return;
    }

    setTouchedFields(prev => ({ ...prev, [name]: true }));

    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 12) return;

      setDetails((prevDetails: any) => ({
        ...prevDetails,
        shippingAddress: {
          ...prevDetails.shippingAddress,
          phone: numericValue,
        },
      }));

      if (numericValue.trim() !== "") {
        setErrors((prev) => ({ ...prev, phone: false }));
      } else {
        setErrors((prev) => ({ ...prev, phone: true }));
      }
      return;
    }

    setDetails((prevDetails: any) => ({
      ...prevDetails,
      shippingAddress: {
        ...prevDetails.shippingAddress,
        [name]: value,
        ...(name === 'country' && { region: '' }),
      },
    }));

    if (name !== 'postCode') {
      if (value?.trim() !== "") {
        setErrors((prev) => ({ ...prev, [name]: false }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: true }));
      }
    }

    if (name === 'country') {
      setTouchedFields(prev => ({ ...prev, region: true }));
      setErrors((prev) => ({ ...prev, region: true }));
    }
  };


  const handleBlur = (e: any) => {
    let name, value;

    if (e?.target) {
      ({ name, value } = e.target);
    } else if (e?.name && e?.value !== undefined) {
      ({ name, value } = e);
    } else {
      console.warn("Unexpected blur event:", e);
      return;
    }

    setTouchedFields(prev => ({ ...prev, [name]: true }));

    const wasPreFilled =
      (initialData?.shippingAddress as any)?.[name] &&
      (initialData.shippingAddress as any)[name].trim() !== '';
    const isEmpty = !value || value.trim() === '';

    if ((wasPreFilled && isEmpty) || isEmpty) {
      setErrors(prev => ({ ...prev, [name]: true }));
    }
  };

  useEffect(() => {
    if (!details?.shippingAddress?.country) {
      setDetails((prevDetails: any) => ({
        ...prevDetails,
        shippingAddress: {
          ...prevDetails?.shippingAddress,
          country: countryFromLocale,
        },
      }));
    }
  }, [countryFromLocale]);

  useEffect(() => {
    if (validateForm) {
      const newErrors: Record<string, boolean> = {};

      requiredFields.forEach(field => {
        const value = (details?.shippingAddress as any)?.[field];
        const isEmpty = !value || value.trim() === '';

        if (isEmpty) {
          newErrors[field] = true;
        }
      });

      setErrors(newErrors);

      // Validate postcode on form submission
      const postcodeValue = details?.shippingAddress?.postCode;
      if (postcodeValue && (postcodeValue.length < 4 || postcodeValue.length > 5)) {
        setPostcodeError('Please enter a valid 4-5 digit postcode');
      } else if (!postcodeValue || postcodeValue.trim() === '') {
        setPostcodeError('Required');
      }

      const allTouched = requiredFields.reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setTouchedFields(allTouched);
    }
  }, [validateForm]);

  const countries = [
    { value: 'AU', label: 'Australia' },
    { value: 'NZ', label: 'New Zealand' },
  ];

  const auStates = [
    { value: 'ACT', label: 'ACT' },
    { value: 'NSW', label: 'NSW' },
    { value: 'NT', label: 'NT' },
    { value: 'QLD', label: 'QLD' },
    { value: 'SA', label: 'SA' },
    { value: 'TAS', label: 'TAS' },
    { value: 'VIC', label: 'VIC' },
    { value: 'WA', label: 'WA' },
  ];

  const nzStates = [
    { value: 'NorthIsland', label: 'North Island' },
    { value: 'SouthIsland', label: 'South Island' },
  ];

  const countryCode = details?.shippingAddress?.country?.toUpperCase();

  const stateOptions =
    countryCode === 'NZ' ? nzStates :
      countryCode === 'AU' ? auStates :
        [];

  const shouldShowError = (fieldName: string) => {
    const value = (details?.shippingAddress as any)?.[fieldName];
    const isEmpty = !value || value.trim() === '';
    const wasPreFilled = (initialData?.shippingAddress as any)?.[fieldName] && (initialData.shippingAddress as any)[fieldName].trim() !== '';

    return errors[fieldName] && isEmpty && (validateForm || wasPreFilled || touchedFields[fieldName]);
  };


  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Mark as touched on first change
    setTouchedFields(prev => ({ ...prev, postCode: true }));

    // Allow only numbers
    if (value && !/^\d*$/.test(value)) {
      return;
    }

    // Limit to 5 digits
    if (value.length > 5) {
      return;
    }

    // Update the form details
    setDetails((prevDetails: any) => ({
      ...prevDetails,
      shippingAddress: {
        ...prevDetails.shippingAddress,
        postCode: value,
      },
    }));

    // Validate immediately on change
    if (value.length === 0) {
      setPostcodeError('Required');
      setErrors((prev) => ({ ...prev, postCode: true }));
    } else if (value.length < 4) {
      setPostcodeError('Please enter a valid 4-5 digit postcode');
      setErrors((prev) => ({ ...prev, postCode: true }));
    } else {
      setPostcodeError('');
      setErrors((prev) => ({ ...prev, postCode: false }));
    }
  };

  return (
    <div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[24px]'>
        <div>
          <InputField
            name="firstName"
            label="First Name*"
            value={details?.shippingAddress?.firstName}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
          />
          {shouldShowError('firstName') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}

        </div>
        <div>
          <InputField
            name="lastName"
            label="Last Name*"
            value={details?.shippingAddress?.lastName}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
          />
          {shouldShowError('lastName') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}
        </div>

        <div>
          <InputField
            name="streetName"
            label="Address*"
            value={details?.shippingAddress?.streetName}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
          />
          {shouldShowError('streetName') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}

        </div>

        <div>
          <InputField
            name="city"
            label="City*"
            value={details?.shippingAddress?.city}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
          />
          {shouldShowError('city') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}

        </div>

        <div>
          <InputField
            name="postCode"
            label="Postcode*"
            value={details?.shippingAddress?.postCode}
            onChange={handlePostcodeChange}
            onBlur={handleBlur}
          />
          {postcodeError && touchedFields.postCode && (
            <span className="text-red-500 text-xs mt-1">{postcodeError}</span>
          )}

        </div>

        <div>
          <SelectField
            name="country"
            label="Country*"
            options={countries}
            value={details?.shippingAddress?.country || countryFromLocale}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
            disabled={true}
          />
          {shouldShowError('country') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}

        </div>

        <div>
          <SelectField
            name="region"
            label="State*"
            options={stateOptions}
            value={details?.shippingAddress?.region}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
          />
          {shouldShowError('region') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}
        </div>

        <div>
          <InputField
            name="phone"
            label="Phone*"
            type="tel"
            value={details?.shippingAddress?.phone}
            onChange={handleShippingAddressChange}
            onBlur={handleBlur}
          />
          {shouldShowError('phone') && (
            <span className="text-red-500 text-xs mt-1">Required</span>
          )}
        </div>
      </div>

    </div>
  );
};

export default AddressesStep;